// Client-safe Insider Bundle config types + resolver. Kept separate from
// product-page-settings.ts (which imports the server-only payload client)
// because the buy box imports these values in the client bundle.

import type { Money, SellingPlan } from '@repo/shopify';

/**
 * One flavor product offered in the bundle grid. Flavors are separate
 * single-variant Shopify products (not variants of one product); the picks are
 * recorded as kit-contents attributes on the Crew Pack line, so the flavor
 * products themselves never enter the cart.
 */
export interface HydrateBundleFlavor {
	handle: string;
	productId: string;
	/** Full Shopify title, e.g. "Workman Hydrate - Fruit Punch" (cart/analytics). */
	title: string;
	/** Short flavor name for slots and tiles, e.g. "Fruit Punch". */
	label: string;
	/** The product's only variant — what the WMS picks for this flavor. */
	merchandiseId: string;
	sku: string | null;
	vendor: string;
	productType: string;
	/** One-time price of a single box (retail-value math fallback). */
	price: Money;
	availableForSale: boolean;
	/** Pack image for slots/tiles (variant image, falling back to featured image). */
	image: { url: string; altText: string | null } | null;
}

/**
 * The Crew Pack kit product — the single Shopify product that actually goes
 * in the cart. It carries the bundle's one-time price ($135), the $180 retail
 * value as its compare-at price, and the ONLY selling plans the bundle offers
 * (Recharge plans live on this product alone; the per-flavor 20% plans are
 * never used by the bundle). The WMS explodes its kit-contents line
 * properties into the individual bag SKUs.
 */
export interface HydrateBundleKitProduct {
	handle: string;
	productId: string;
	title: string;
	/** The kit product's only variant — the one cart line for the whole bundle. */
	merchandiseId: string;
	sku: string | null;
	vendor: string;
	productType: string;
	/** One-time bundle price. */
	price: Money;
	/** Full retail value of the bundle (e.g. $180 for 4 × $45). */
	compareAtPrice: Money | null;
	availableForSale: boolean;
	image: { url: string; altText: string | null } | null;
	/** Recurring plans on the kit product (Recharge). */
	sellingPlans: SellingPlan[];
}

/** The complete bundle offer resolved for a PDP. */
export interface HydrateBundleOffer {
	config: InsiderBundleSettings;
	flavors: HydrateBundleFlavor[];
	kitProduct: HydrateBundleKitProduct;
}

/** The `insiderBundle` group of the product-page-settings global, normalized. */
export interface InsiderBundleSettings {
	enabled: boolean;
	title: string;
	description: string;
	/** Product types whose PDPs show the bundle tab. */
	productTypes: string[];
	/** Flavor products offered in the grid; also scope the tab onto their PDPs. */
	handles: string[];
	/** Handle of the Crew Pack kit product added to the cart. */
	kitProductHandle: string;
	sticksPerBox: number;
	/** Paid boxes the customer picks. */
	boxCount: number;
	/** Free bonus boxes (matches the Shopify Buy-X-Get-Y automatic discount). */
	bonusCount: number;
	oneBoxTabLabel: string;
	bundleTabLabel: string;
	bestValueBadge: string;
	bonusSlotLabel: string;
	/** Bundle CTA label while subscribing; empty falls back to the Subscribe & Save CTA. */
	subscribeCtaLabel: string;
}

export const DEFAULT_INSIDER_BUNDLE_SETTINGS: InsiderBundleSettings = {
	enabled: false,
	title: 'Crew Pack',
	description: '',
	productTypes: [],
	handles: [],
	kitProductHandle: 'workman-hydrate-crew-pack',
	sticksPerBox: 32,
	boxCount: 3,
	bonusCount: 1,
	oneBoxTabLabel: 'One bag',
	bundleTabLabel: 'Crew Pack',
	bestValueBadge: 'Best Value',
	bonusSlotLabel: 'Bonus Bag',
	subscribeCtaLabel: '',
};

/** Tolerant Shopify product-type matching (web parity: normalizeProductTypeKey). */
function normalizeTypeKey(value: string | null | undefined): string {
	return (
		value
			?.trim()
			.replace(/[‐‑–—−]/g, '-')
			.replace(/\s+/g, ' ')
			.toLowerCase() || ''
	);
}

/**
 * Whether this product's PDP shows the Insider Bundle tab: the settings when
 * enabled and the product matches by type or is itself one of the bundle
 * flavors, else null (the buy box renders exactly as before).
 */
export function resolveInsiderBundleScope(
	settings: InsiderBundleSettings | null | undefined,
	productHandle: string | null | undefined,
	productType?: string | null,
): InsiderBundleSettings | null {
	if (!settings?.enabled) return null;

	const handle = productHandle?.trim().toLowerCase() || '';
	const typeKey = normalizeTypeKey(productType);
	const matchesType =
		typeKey.length > 0 &&
		settings.productTypes.some((entry) => normalizeTypeKey(entry) === typeKey);
	const matchesHandle = handle.length > 0 && settings.handles.includes(handle);
	if (!matchesType && !matchesHandle) return null;

	return settings;
}
